'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Typography,
  Link,
  styled,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InputMask from 'react-input-mask';

// Типизация для InputMask
const MaskedInput = InputMask as unknown as React.ComponentType<{
  mask: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  disabled: boolean;
  children: (inputProps: React.InputHTMLAttributes<HTMLInputElement>) => React.ReactElement;
}>;
import { config, TELEGRAM_BOT, TELEGRAM_BOT_NAME } from '../../../config';
import { usePaymentStorage } from '../../../hooks/usePaymentStorage';
import { useGuestAccountSearch } from '../../../hooks/useGuestAccountSearch';
import { useAccountNumberSearch } from '../../../hooks/useAccountNumberSearch';
import type { PaymentPrefill } from '../../../utils/paymentPrefill';
import { AccountSelectorDropdown, AccountDropdownOption } from './AccountSelectorDropdown';
import { AnnualPaymentHint } from './AnnualPaymentHint';
import {
  buildGuestPaymentSchema,
  getGuestMaxAmount,
  GUEST_PHONE_MASKED_REGEX,
  type GuestIdentifyBy,
  type GuestPaymentFormData,
  type GuestPaymentFormValues,
} from './guestPaymentSchema';
// @ts-ignore
import telegramIcon from '@app/assets/telegram.png';

export { buildGuestPaymentSchema } from './guestPaymentSchema';
export type { GuestIdentifyBy, GuestPaymentFormData } from './guestPaymentSchema';

/**
 * Данные для отправки на сервер
 */
export interface PaymentDataGuest {
  /** ID выбранного лицевого счёта */
  accountId: string;
  /** Телефон в формате +7XXXXXXXXXX (только в режиме идентификации по телефону) */
  phone?: string;
  amount: number;
  email?: string;
  /** Признак годовой оплаты по акции (из ссылки Telegram-бота); сервер проверяет сумму сам */
  annual?: boolean;
}

/**
 * Свойства компонента PaymentFormGuest
 */
export interface PaymentFormGuestProps {
  /** Обработчик отправки формы (оплата выбранного счёта) */
  onSubmit: (data: PaymentDataGuest) => Promise<void>;
  /** Флаг загрузки */
  isLoading?: boolean;
  /** Сообщение об ошибке */
  error?: string | null;
  /** Флаг открытия модального окна */
  open?: boolean;
  /** Обработчик закрытия модального окна */
  onClose?: () => void;
  /**
   * Данные предзаполнения из URL (ссылка Telegram-бота): номер ЛС, сумма, признак годовой оплаты.
   * Имеют приоритет над сохранёнными в localStorage данными; все поля остаются редактируемыми.
   */
  prefill?: PaymentPrefill;
}

/**
 * Проверяет, что в поле суммы есть число (пустое поле хранится как NaN или undefined).
 *
 * @param value - значение поля суммы
 * @returns true, если значение — число, не NaN
 */
const hasAmountValue = (value: number | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/**
 * Стилизованное модальное окно
 */
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '480px',
    maxWidth: '90%',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',

    [theme.breakpoints.down('sm')]: {
      width: '90%',
      margin: '16px',
    },
  },
}));

/**
 * Стилизованный заголовок диалога
 */
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '12px 32px',
  fontSize: '24px',
  fontWeight: 600,
  fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#1e1e1e',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,

  [theme.breakpoints.down('sm')]: {
    padding: '16px 20px',
    fontSize: '20px',
  },
}));

/**
 * Стилизованный контент диалога
 */
const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '32px',
  paddingTop: '24px',

  [theme.breakpoints.down('sm')]: {
    padding: '24px',
    paddingTop: '20px',
  },
}));

/**
 * Контейнер формы
 */
const FormContainer = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  paddingTop: '24px',

  [theme.breakpoints.down('sm')]: {
    gap: '20px',
  },
}));

/**
 * Стилизованная кнопка отправки
 */
const SubmitButton = styled(Button)(({ theme }) => ({
  height: '48px',
  textTransform: 'none',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',
  backgroundColor: '#1976d2',
  color: '#ffffff',

  '&:hover': {
    backgroundColor: '#1565c0',
  },

  '&:disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
  },

  [theme.breakpoints.down('sm')]: {
    height: '44px',
    fontSize: '15px',
  },
}));

/**
 * Стилизованное текстовое поле
 */
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',

    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
    },

    '&:hover fieldset': {
      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
    },

    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },

    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },

  '& .MuiInputLabel-root': {
    fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',

    [theme.breakpoints.down('sm')]: {
      fontSize: '15px',
    },
  },

  '& .MuiFormHelperText-root': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
    },
  },
}));

/**
 * Компонент формы оплаты для неавторизованных пользователей
 * 
 * Отображает модальное окно с формой для ввода номера телефона (или номера лицевого счёта),
 * суммы пополнения и опционального email. Автоматически сохраняет данные в localStorage.
 *
 * Предзаполнение из ссылки Telegram-бота (`prefill`): с номером ЛС форма стартует в режиме
 * идентификации по номеру ЛС, сумма подставляется в поле суммы, при `annual` показывается
 * подсказка «Оплата за год по акции», а признак `annual` передаётся в `onSubmit`.
 * Данные из URL важнее сохранённых в localStorage; все поля остаются редактируемыми.
 * 
 * @example
 * ```tsx
 * <PaymentFormGuest
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={async (data) => {
 *     await generatePayment(data);
 *   }}
 *   isLoading={isGenerating}
 *   error={errorMessage}
 * />
 * ```
 */
export function PaymentFormGuest({
  onSubmit,
  isLoading = false,
  error = null,
  open = true,
  onClose,
  prefill,
}: PaymentFormGuestProps) {
  const { data: storageData, updateData, isLoaded } = usePaymentStorage();
  const [showEmailField, setShowEmailField] = useState(false);

  // Годовая оплата по акции (ссылка Telegram-бота)
  const annual = prefill?.annual === true;
  const maxAmount = getGuestMaxAmount(annual);

  // Режим идентификации: с номером ЛС из URL стартуем по номеру ЛС, иначе по телефону
  const [identifyBy, setIdentifyBy] = useState<GuestIdentifyBy>(
    prefill?.accountNumber ? 'accountNumber' : 'phone'
  );

  // Схема пересоздаётся при смене режима идентификации или признака annual
  const schema = useMemo(() => buildGuestPaymentSchema({ identifyBy, annual }), [identifyBy, annual]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
    clearErrors,
  } = useForm<GuestPaymentFormValues, undefined, GuestPaymentFormData>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      phone: '',
      accountNumber: prefill?.accountNumber ?? '',
      // Сумма из URL (Req 7.2); без неё поле пустое (Req 7.3)
      amount: prefill?.amount,
      email: '',
    },
  });

  // Загрузка данных из localStorage: данные из URL (номер ЛС, сумма) имеют приоритет
  useEffect(() => {
    if (isLoaded && storageData) {
      if (storageData.phone) {
        setValue('phone', storageData.phone);
      }
      if (storageData.amount && prefill?.amount === undefined) {
        setValue('amount', storageData.amount);
      }
      if (storageData.email) {
        setValue('email', storageData.email);
        setShowEmailField(true);
      }
    }
  }, [isLoaded, storageData, setValue, prefill?.amount]);

  // Отслеживание изменений полей для показа email
  const phoneValue = watch('phone');
  const accountNumberValue = watch('accountNumber');
  const amountValue = watch('amount');

  // Автоматический поиск счетов (без кнопки, с debounce): в неактивном режиме запрос не выполняется
  const phoneSearch = useGuestAccountSearch(identifyBy === 'phone' ? phoneValue || '' : '');
  const accountNumberSearch = useAccountNumberSearch(
    identifyBy === 'accountNumber' ? accountNumberValue : undefined
  );
  const { accounts, isSearching, notFound } =
    identifyBy === 'phone' ? phoneSearch : accountNumberSearch;
  const searchFailed = identifyBy === 'accountNumber' && accountNumberSearch.isError;

  // Выбранный клиентом лицевой счёт
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  // Автовыбор счёта: если найден один — выбираем его; при нескольких — по точному совпадению
  // номера ЛС (режим accountNumber); если изменился список — сбрасываем невалидный выбор
  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedAccountId(accounts[0].id);
    } else if (accounts.length === 0) {
      setSelectedAccountId('');
    } else if (!accounts.some((acc) => acc.id === selectedAccountId)) {
      const exact =
        identifyBy === 'accountNumber'
          ? accounts.find((acc) => acc.accountNumber === accountNumberValue.trim())
          : undefined;
      setSelectedAccountId(exact?.id ?? '');
    }
  }, [accounts, selectedAccountId, identifyBy, accountNumberValue]);

  /**
   * Переключает режим идентификации счёта (телефон ↔ номер ЛС).
   * Введённые значения сохраняются, ошибки валидации и выбранный счёт сбрасываются.
   */
  const handleToggleIdentifyBy = useCallback(() => {
    setIdentifyBy((current) => (current === 'phone' ? 'accountNumber' : 'phone'));
    setSelectedAccountId('');
    clearErrors();
  }, [clearErrors]);

  // Опции для выпадающего списка счетов (используем маскированный адрес)
  const accountOptions: AccountDropdownOption[] = accounts.map((acc) => ({
    id: acc.id,
    accountNumber: acc.accountNumber,
    address: acc.maskedAddress,
    balance: acc.balance ?? 0,
    isBlocked: acc.isBlocked,
  }));

  const hasAccounts = accounts.length > 0;

  useEffect(() => {
    // Показываем поле email, если заполнены идентификатор счёта (телефон или номер ЛС) и сумма
    const identifierValid =
      identifyBy === 'phone'
        ? GUEST_PHONE_MASKED_REGEX.test(phoneValue || '')
        : config.payment.accountNumberPattern.test((accountNumberValue || '').trim());
    const amountValid = !!amountValue && amountValue >= config.payment.minAmount && amountValue <= maxAmount;

    if (identifierValid && amountValid) {
      setShowEmailField(true);
    }
  }, [identifyBy, phoneValue, accountNumberValue, amountValue, maxAmount]);

  /**
   * Обработчик отправки формы
   * Мемоизирован для предотвращения создания новой функции при каждом рендере
   */
  const handleFormSubmit = useCallback(async (data: GuestPaymentFormData) => {
    try {
      // Телефон передаётся только в режиме идентификации по телефону (формат +7XXXXXXXXXX)
      let formattedPhone: string | undefined;
      if (identifyBy === 'phone') {
        const cleanPhone = (data.phone ?? '').replace(/\D/g, '');

        // Дополнительная валидация телефона
        if (cleanPhone.length !== 11 || !cleanPhone.startsWith('7')) {
          throw new Error('Неверный формат номера телефона');
        }

        formattedPhone = `+${cleanPhone}`;
      }

      // Должен быть выбран лицевой счёт
      if (!selectedAccountId) {
        throw new Error('Выберите лицевой счёт для оплаты');
      }

      // Валидация суммы (для годовой оплаты лимит выше; меньше годовой суммы не блокируем —
      // сервер сам проверит и создаст обычный платёж)
      if (data.amount < config.payment.minAmount || data.amount > maxAmount) {
        throw new Error(`Сумма должна быть от ${config.payment.minAmount} до ${maxAmount} ${config.payment.currencySymbol}`);
      }

      const paymentData: PaymentDataGuest = {
        accountId: selectedAccountId,
        phone: formattedPhone,
        amount: data.amount,
        email: data.email || undefined,
        annual,
      };

      await onSubmit(paymentData);
    } catch (err) {
      // Обработка ошибок оплаты (Требование 9.2)
      console.error('Ошибка при обработке платежа:', err);

      // Пробрасываем ошибку дальше для обработки в родительском компоненте
      throw err;
    }
  }, [onSubmit, selectedAccountId, identifyBy, maxAmount, annual]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <StyledDialogTitle>
        Оплата услуг
        {onClose && (
          <IconButton
            aria-label="закрыть"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogTitle>

      <StyledDialogContent>
        <FormContainer onSubmit={handleSubmit(handleFormSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Поле номера телефона (режим идентификации по телефону) */}
          {identifyBy === 'phone' && (
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <MaskedInput
                  mask="+7 (999) 999-99-99"
                  value={field.value ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    // Автосохранение в localStorage
                    updateData({ phone: e.target.value });
                  }}
                  onBlur={field.onBlur}
                  disabled={isLoading}
                >
                  {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                    <StyledTextField
                      {...(inputProps as Record<string, unknown>)}
                      label="Номер телефона"
                      placeholder={config.validation.phone.placeholder}
                      error={isSubmitted && !!errors.phone}
                      helperText={isSubmitted && errors.phone?.message ? errors.phone.message : 'Введите номер телефона'}
                      fullWidth
                      required
                    />
                  )}
                </MaskedInput>
              )}
            />
          )}

          {/* Поле номера лицевого счёта (режим идентификации по номеру ЛС, значение из ссылки редактируется) */}
          {identifyBy === 'accountNumber' && (
            <Controller
              name="accountNumber"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  value={field.value ?? ''}
                  label="Номер лицевого счёта"
                  placeholder="Например, 12345678"
                  inputProps={{ inputMode: 'numeric', maxLength: 20 }}
                  error={isSubmitted && !!errors.accountNumber}
                  helperText={
                    isSubmitted && errors.accountNumber?.message
                      ? errors.accountNumber.message
                      : 'Номер указан в договоре и в Telegram-боте'
                  }
                  fullWidth
                  required
                  disabled={isLoading}
                  onChange={(e) => {
                    // Только цифры: номер ЛС состоит из цифр (config.payment.accountNumberPattern)
                    field.onChange(e.target.value.replace(/\D/g, ''));
                  }}
                />
              )}
            />
          )}

          {/* Переключатель режима идентификации: телефон ↔ номер ЛС */}
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={handleToggleIdentifyBy}
            disabled={isLoading}
            sx={{ alignSelf: 'flex-start', mt: -1.5 }}
          >
            {identifyBy === 'phone' ? 'Ввести номер лицевого счёта' : 'Искать по телефону'}
          </Link>

          {/* Ссылка на Telegram-бота: удобная оплата и контроль баланса в Telegram */}
          {TELEGRAM_BOT && (
            <Link
              href={TELEGRAM_BOT}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: -1.5,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <img src={telegramIcon} width={20} height={20} alt="Telegram" />
              <Typography variant="body2">
                Оплата и баланс в Telegram-боте{TELEGRAM_BOT_NAME ? ` ${TELEGRAM_BOT_NAME.toLowerCase()}` : ''}
              </Typography>
            </Link>
          )}

          {/* Индикатор автоматического поиска счетов (по телефону или по номеру ЛС) */}
          {isSearching && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Ищем ваши лицевые счета...
              </Typography>
            </Box>
          )}

          {/* Сообщение, если счета не найдены */}
          {notFound && !isSearching && (
            <Alert severity="info">
              {identifyBy === 'phone'
                ? 'Лицевые счета по этому номеру не найдены. Проверьте правильность номера телефона.'
                : 'Лицевой счёт не найден. Проверьте номер или найдите по телефону'}
            </Alert>
          )}

          {/* Ошибка запроса поиска по номеру ЛС (сеть, сервер, лимиты запросов) */}
          {searchFailed && !isSearching && (
            <Alert severity="warning">
              Не удалось выполнить поиск лицевого счёта. Попробуйте позже или найдите по телефону.
            </Alert>
          )}

          {/* Список найденных счетов с балансом и маскированным адресом.
              Клиент выбирает, на какой счёт пополнять. */}
          {hasAccounts && (
            <AccountSelectorDropdown
              accounts={accountOptions}
              selectedAccountId={selectedAccountId}
              onAccountChange={setSelectedAccountId}
              disabled={isLoading}
              label="Выберите лицевой счёт"
            />
          )}

          {/* Подсказка годовой оплаты по акции (только при annual в ссылке бота) */}
          {annual && <AnnualPaymentHint />}

          {/* Поле суммы пополнения (после выбора счёта) */}
          {hasAccounts && (
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                value={hasAmountValue(field.value) ? field.value : ''}
                label="Сумма пополнения"
                type="number"
                placeholder={hasAmountValue(field.value) ? '' : `${config.payment.minAmount}-${maxAmount}`}
                error={isSubmitted && !!errors.amount}
                helperText={
                  isSubmitted && errors.amount?.message
                    ? errors.amount.message
                    : `Минимум ${config.payment.minAmount} ${config.payment.currencySymbol}, максимум ${maxAmount} ${config.payment.currencySymbol}`
                }
                fullWidth
                required
                disabled={isLoading}
                onChange={(e) => {
                  const value = e.target.value ? Number(e.target.value) : Number.NaN;
                  // Пустое значение хранится как NaN: yup выдаст «Введите сумму», а react-hook-form
                  // не подставит обратно предзаполненную сумму (undefined откатывается к defaultValue)
                  field.onChange(value);
                  // Автосохранение в localStorage (сумма из URL сохраняется только после правки клиентом)
                  if (value) {
                    updateData({ amount: value });
                  }
                }}
                InputLabelProps={{
                  shrink: hasAmountValue(field.value) ? true : undefined,
                }}
                InputProps={{
                  endAdornment: <Box sx={{ ml: 1, color: 'text.secondary' }}>{config.payment.currencySymbol}</Box>,
                }}
              />
            )}
          />
          )}

          {/* Условное поле email (после выбора счёта) */}
          {hasAccounts && showEmailField && (
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <StyledTextField
                  {...field}
                  value={field.value ?? ''}
                  label="Email (необязательно)"
                  type="email"
                  placeholder="example@mail.ru"
                  error={isSubmitted && !!errors.email}
                  helperText={isSubmitted && errors.email?.message ? errors.email.message : 'Для отправки чека об оплате'}
                  fullWidth
                  disabled={isLoading}
                  onChange={(e) => {
                    field.onChange(e);
                    // Автосохранение в localStorage
                    updateData({ email: e.target.value });
                  }}
                />
              )}
            />
          )}

          {/* Кнопка отправки: активна только когда выбран лицевой счёт */}
          <SubmitButton
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !hasAccounts || !selectedAccountId}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Перейти к оплате'
            )}
          </SubmitButton>
        </FormContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
}
